import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { agoraAPI, userAPI } from '../utils/api';
import { stopOutgoingTone, stopRingtone, stopVibration } from '../utils/soundManager';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { toast } from 'sonner';
import {
  Mic, MicOff, Video as VideoIcon, VideoOff,
  PhoneOff, Monitor, MonitorOff, RotateCcw,
  UserPlus, Volume2, Headphones, SwitchCamera, X,
} from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback } from './ui/avatar';

const MAX_USERS = 6;

// ── Remote Video Player ──
const RemoteVideoPlayer = ({ videoTrack, audioTrack, uid, userName }) => {
  const containerRef = useRef(null);
  useEffect(() => {
    if (!containerRef.current || !videoTrack) return;
    while (containerRef.current.firstChild) containerRef.current.removeChild(containerRef.current.firstChild);
    videoTrack.play(containerRef.current);
    // Ensure Agora-inserted video element fills the container properly
    setTimeout(() => {
      if (containerRef.current) {
        const videos = containerRef.current.querySelectorAll('video, div');
        videos.forEach(v => {
          v.style.width = '100%';
          v.style.height = '100%';
          v.style.objectFit = 'cover';
          v.style.position = 'absolute';
          v.style.top = '0';
          v.style.left = '0';
          v.style.borderRadius = '0.5rem';
        });
      }
    }, 50);
    return () => { try { videoTrack.stop(); } catch (e) { console.warn('[RemoteVideo] Stop error:', e); } };
  }, [videoTrack]);

  useEffect(() => {
    if (!audioTrack) return;
    const play = () => { try { audioTrack.play(); } catch (e) { console.warn('[RemoteAudio] Play error:', e); } };
    play();
    const resume = () => { play(); document.removeEventListener('click', resume); document.removeEventListener('touchstart', resume); };
    document.addEventListener('click', resume, { once: true });
    document.addEventListener('touchstart', resume, { once: true });
    return () => { try { audioTrack.stop(); } catch (e) { console.warn('[RemoteAudio] Stop error:', e); } document.removeEventListener('click', resume); document.removeEventListener('touchstart', resume); };
  }, [audioTrack]);

  const displayName = userName || `User ${uid}`;

  return (
    <div className="video-container relative overflow-hidden rounded-lg bg-black" style={{ minHeight: '200px', width: '100%' }} data-testid={`remote-video-${uid}`}>
      <div ref={containerRef} className="absolute inset-0 bg-black rounded-lg" style={{ minHeight: '200px' }}></div>
      {!videoTrack && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="text-white text-center">
            <div className="w-16 h-16 rounded-full bg-[#4338CA] mx-auto flex items-center justify-center text-xl font-bold mb-1">
              {displayName.charAt(0).toUpperCase()}
            </div>
            <p className="text-xs text-white/60 mt-1">{displayName}</p>
          </div>
        </div>
      )}
      <div className="absolute bottom-2 left-2 bg-black/70 px-2.5 py-0.5 rounded-full text-white text-xs font-medium backdrop-blur-sm">
        {displayName}
      </div>
    </div>
  );
};

// ── Control Button ──
const ControlButton = ({ onClick, active, danger, large, small, children, testId }) => {
  let bg = 'bg-white/20 border-white/30';
  if (active) bg = 'bg-green-500 border-green-400';
  if (danger) bg = 'bg-red-500 border-red-400';
  const size = large ? 'w-16 h-16' : small ? 'w-11 h-11' : 'w-14 h-14';
  return (
    <button onClick={onClick} className={`flex items-center justify-center rounded-full ${bg} text-white border backdrop-blur-md active:scale-95 transition-transform shadow-lg ${size}`} data-testid={testId}>
      {children}
    </button>
  );
};

// ── Main VideoCallView ──
const VideoCallView = ({ socket, onEndCall }) => {
  const { channelId } = useParams();
  const [searchParams] = useSearchParams();
  const isVideo = searchParams.get('video') === 'true';
  const { user } = useAuth();
  const navigate = useNavigate();

  const [remoteUsers, setRemoteUsers] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(!isVideo);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isSpeaker, setIsSpeaker] = useState(true);
  const [cameras, setCameras] = useState([]);
  const [currentCameraIdx, setCurrentCameraIdx] = useState(0);
  const [facingMode, setFacingMode] = useState('user');
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [aiNotesEnabled, setAiNotesEnabled] = useState(null);
  const [uidNameMap, setUidNameMap] = useState({});

  const [callStatus, setCallStatus] = useState('initializing');
  const [callError, setCallError] = useState(null);
  const [joinAttempts, setJoinAttempts] = useState(0);

  const clientRef = useRef(null);
  const localVideoRef = useRef(null);
  const localVideoTrackRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const screenTrackRef = useRef(null);
  const mountedRef = useRef(true);
  const joinedRef = useRef(false);
  const endedRef = useRef(false);

  // Random UID for this session (avoids collision)
  const uidRef = useRef(Math.floor(Math.random() * 100000) + 1);

  // Consistent channel name: call_<channelId>
  const agoraChannelName = `call_${channelId}`;

  const upsertRemoteUser = useCallback((uid, patch) => {
    setRemoteUsers(prev => {
      const idx = prev.findIndex(u => u.uid === uid);
      if (idx >= 0) { const up = [...prev]; up[idx] = { ...up[idx], ...patch }; return up; }
      // Enforce max users limit (MAX_USERS - 1 remote since local is 1)
      if (prev.length >= MAX_USERS - 1) return prev;
      return [...prev, { uid, videoTrack: null, audioTrack: null, ...patch }];
    });
  }, []);

  const removeRemoteUser = useCallback((uid) => {
    setRemoteUsers(prev => prev.filter(u => u.uid !== uid));
  }, []);

  const playLocalVideo = useCallback((track) => {
    const el = localVideoRef.current;
    if (!el || !track) return;
    while (el.firstChild) el.removeChild(el.firstChild);
    track.play(el);
    // Ensure video elements use object-fit: cover
    const videos = el.querySelectorAll('video');
    videos.forEach(v => { v.style.objectFit = 'cover'; v.style.borderRadius = '0.5rem'; });
  }, []);

  // ── Load cameras on mount ──
  useEffect(() => {
    AgoraRTC.getCameras().then(cams => {
      setCameras(cams);
    }).catch((e) => { console.warn('[Agora] Camera enumeration failed:', e); });
  }, []);

  // ── Mount: start Agora immediately (user already accepted or is the caller) ──
  useEffect(() => {
    mountedRef.current = true;
    endedRef.current = false;
    initializeAgora();
    return () => {
      mountedRef.current = false;
      cleanup();
      // Ensure call manager state resets when component unmounts unexpectedly
      if (!endedRef.current && onEndCall) onEndCall(channelId);
    };
  }, []);

  // ── Broadcast our UID→name and listen for others' ──
  useEffect(() => {
    if (!socket || callStatus !== 'connected') return;
    // Announce ourselves
    socket.emit('call_join_info', {
      channel_id: channelId,
      agora_uid: uidRef.current,
      user_name: user?.name || 'Unknown',
    });
    // Listen for other users' name announcements
    const handleUpdate = (data) => {
      if (data?.type === 'call_user_info' && data.data?.channel_id === channelId) {
        setUidNameMap(prev => ({ ...prev, [data.data.agora_uid]: data.data.user_name }));
      }
    };
    socket.on('update', handleUpdate);
    return () => socket.off('update', handleUpdate);
  }, [socket, callStatus, channelId, user?.name]);

  // ── AI Notes: ask once at call start ──
  useEffect(() => {
    if (callStatus !== 'connected' || aiNotesEnabled !== null) return;
    const saved = localStorage.getItem('ai_notes_preference');
    if (saved !== null) {
      setAiNotesEnabled(saved === 'true');
    } else {
      const enable = window.confirm('Enable AI Meeting Notes for this call?');
      setAiNotesEnabled(enable);
      localStorage.setItem('ai_notes_preference', String(enable));
    }
  }, [callStatus, aiNotesEnabled]);

  // ── Permissions ──
  const requestMediaPermissions = async () => {
    setCallStatus('requesting-permissions');
    try {
      const c = { audio: true };
      if (isVideo) c.video = true;
      const stream = await navigator.mediaDevices.getUserMedia(c);
      stream.getTracks().forEach(t => t.stop());
      return true;
    } catch (err) {
      if (err.name === 'NotAllowedError') setCallError('Camera/Mic permission denied. Allow access and tap Retry.');
      else if (err.name === 'NotFoundError') setCallError('No camera/mic found. Connect a device and tap Retry.');
      else setCallError(`Media error: ${err.message}`);
      setCallStatus('failed');
      return false;
    }
  };

  // ── Init Agora (up to 3 attempts: 1s, 2s, 3s) ──
  const initializeAgora = async (retryCount = 0) => {
    if (joinedRef.current) return;

    const appId = process.env.REACT_APP_AGORA_APP_ID;
    if (!appId) { setCallError('Agora App ID not configured.'); setCallStatus('failed'); return; }

    if (retryCount === 0) {
      const ok = await requestMediaPermissions();
      if (!ok) return;
    }

    setCallStatus('joining');
    setJoinAttempts(retryCount + 1);

    try {
      const uid = uidRef.current;

      // Clean previous
      await cleanupTracks();
      if (clientRef.current) { try { await clientRef.current.leave(); } catch (e) { console.warn('[Agora] Leave error:', e); } clientRef.current = null; }

      // Token (fallback null for dev)
      let token = null;
      try {
        const { data } = await agoraAPI.getToken(agoraChannelName, uid);
        token = data.token;
      } catch (e) { console.warn('[Agora] Token fetch failed, using null:', e.message); }

      // Fresh client
      const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
      clientRef.current = client;

      // Enable dual-stream for group call bandwidth optimization
      try {
        await client.enableDualStream();
        client.setLowStreamParameter({ width: 160, height: 120, framerate: 15, bitrate: 120 });
      } catch (e) { console.warn('[Agora] Dual-stream setup:', e); }

      // Token renewal
      client.on('token-privilege-will-expire', async () => {
        try { const { data } = await agoraAPI.getToken(agoraChannelName, uid); await client.renewToken(data.token); toast.success('Token renewed'); } catch (e) { console.error('[Agora] Token renewal error:', e); if (mountedRef.current) { setCallError('Token renewal failed.'); setCallStatus('failed'); } }
      });
      client.on('token-privilege-did-expire', async () => {
        if (mountedRef.current) setCallStatus('reconnecting');
        try { const { data } = await agoraAPI.getToken(agoraChannelName, uid); await client.join(appId, agoraChannelName, data.token, uid); if (mountedRef.current) { setCallStatus('connected'); toast.success('Reconnected'); } } catch (e) { console.error('[Agora] Token expired reconnect error:', e); if (mountedRef.current) { setCallError('Token expired.'); setCallStatus('failed'); } }
      });
      client.on('connection-state-change', (cur, prev, reason) => {
        if (!mountedRef.current) return;
        if (cur === 'DISCONNECTED' && reason === 'NETWORK_ERROR') setCallStatus('reconnecting');
        if (cur === 'CONNECTED' && prev === 'RECONNECTING') { setCallStatus('connected'); toast.success('Reconnected'); }
      });

      // Remote tracks
      client.on('user-published', async (ru, type) => {
        let subscribed = false;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            await client.subscribe(ru, type);
            subscribed = true;
            break;
          } catch (e) {
            console.warn(`[Agora] Subscribe attempt ${attempt + 1} failed:`, e.message);
            await new Promise(r => setTimeout(r, 1000));
          }
        }
        if (!subscribed) { console.error('[Agora] Failed to subscribe after 3 attempts'); return; }
        if (type === 'video') {
          upsertRemoteUser(ru.uid, { videoTrack: ru.videoTrack });
          try {
            const remoteCount = client.remoteUsers.length;
            if (remoteCount >= 3) {
              await client.setRemoteVideoStreamType(ru.uid, 1);
            }
          } catch (e) { console.warn('[Agora] Stream type switch:', e); }
        }
        if (type === 'audio') upsertRemoteUser(ru.uid, { audioTrack: ru.audioTrack });
      });
      client.on('user-unpublished', (ru, type) => {
        if (type === 'video') upsertRemoteUser(ru.uid, { videoTrack: null });
        if (type === 'audio') upsertRemoteUser(ru.uid, { audioTrack: null });
      });
      client.on('user-left', (ru) => removeRemoteUser(ru.uid));
      client.on('user-joined', (ru) => {
      });

      // JOIN
      await client.join(appId, agoraChannelName, token, uid);
      if (!mountedRef.current) return;
      joinedRef.current = true;
      setCallStatus('connected');
      setCallError(null);

      // Stop all ringtones/outgoing tones — Agora is now connected
      // This handles cases where socket 'call_accepted' event was delayed or lost
      stopOutgoingTone();
      stopRingtone();
      stopVibration();

      // Local tracks
      const audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
      localAudioTrackRef.current = audioTrack;
      const toPublish = [audioTrack];

      if (isVideo) {
        try {
          const videoTrack = await AgoraRTC.createCameraVideoTrack();
          localVideoTrackRef.current = videoTrack;
          playLocalVideo(videoTrack);
          toPublish.push(videoTrack);
        } catch (e) { toast.info('Camera unavailable — audio only'); console.warn('[Agora] Camera init error:', e); if (mountedRef.current) setIsVideoOff(true); }
      }

      await client.publish(toPublish);
      toast.success('Call connected');

      // Subscribe to any remote users who were already in the channel before we joined
      const subscribeToExistingUsers = async () => {
        for (const ru of client.remoteUsers) {
          if (ru.hasVideo) {
            try {
              await client.subscribe(ru, 'video');
              upsertRemoteUser(ru.uid, { videoTrack: ru.videoTrack });
            } catch (e) { console.warn('[Agora] Video subscribe failed:', e); }
          }
          if (ru.hasAudio) {
            try {
              await client.subscribe(ru, 'audio');
              upsertRemoteUser(ru.uid, { audioTrack: ru.audioTrack });
            } catch (e) { console.warn('[Agora] Audio subscribe failed:', e); }
          }
        }
      };
      await subscribeToExistingUsers();
      // Retry after a short delay (remote user might publish after we join)
      setTimeout(() => { if (mountedRef.current) subscribeToExistingUsers(); }, 2000);
      setTimeout(() => { if (mountedRef.current) subscribeToExistingUsers(); }, 5000);

    } catch (error) {
      console.error(`[Agora] Init failed (attempt ${retryCount + 1}):`, error);
      if (!mountedRef.current) return;
      if (retryCount < 2) {
        const delay = (retryCount + 1) * 1000;
        toast.info(`Retrying in ${delay / 1000}s...`);
        setCallStatus('reconnecting');
        setTimeout(() => { if (mountedRef.current) initializeAgora(retryCount + 1); }, delay);
      } else {
        setCallError(`Failed after 3 attempts: ${error.message || 'Unknown'}.`);
        setCallStatus('failed');
      }
    }
  };

  const handleRetry = async () => {
    setCallError(null); setCallStatus('initializing');
    await cleanup();
    initializeAgora(0);
  };

  const cleanupTracks = async () => {
    [localVideoTrackRef, localAudioTrackRef, screenTrackRef].forEach(ref => {
      const t = ref.current;
      if (t) { try { t.stop(); t.close(); } catch (e) { console.warn('[Agora] Track cleanup error:', e); } ref.current = null; }
    });
  };

  const cleanup = async () => {
    await cleanupTracks();
    if (clientRef.current) { 
      try { 
        clientRef.current.removeAllListeners();
        await clientRef.current.leave(); 
      } catch (e) { console.warn('[Agora] Leave error:', e); } 
      clientRef.current = null; 
    }
    joinedRef.current = false;
    uidRef.current = Math.floor(Math.random() * 100000) + 1;
    setRemoteUsers([]);
  };

  // ── Controls ──
  const toggleMute = async () => {
    const t = localAudioTrackRef.current; if (!t) return;
    await t.setEnabled(isMuted); setIsMuted(!isMuted);
  };

  const toggleVideo = async () => {
    const client = clientRef.current; if (!client) return;
    if (isVideoOff) {
      try {
        let t = localVideoTrackRef.current;
        if (!t) { t = await AgoraRTC.createCameraVideoTrack(); localVideoTrackRef.current = t; await client.publish(t); }
        else { await t.setEnabled(true); }
        playLocalVideo(t); setIsVideoOff(false);
      } catch (e) { console.error('[Agora] Camera enable error:', e); toast.error('Failed to enable camera'); }
    } else {
      const t = localVideoTrackRef.current;
      if (t) { await t.setEnabled(false); setIsVideoOff(true); }
    }
  };

  const toggleScreenShare = async () => {
    const client = clientRef.current; if (!client) return;
    if (!isScreenSharing) {
      try {
        const result = await AgoraRTC.createScreenVideoTrack({ encoderConfig: '1080p_1' }, 'disable');
        const sv = Array.isArray(result) ? result[0] : result;
        screenTrackRef.current = sv;
        const cam = localVideoTrackRef.current; if (cam) await client.unpublish(cam);
        await client.publish(sv);
        sv.on('track-ended', () => stopScreenShare());
        setIsScreenSharing(true);
      } catch (e) { if (e.code !== 'PERMISSION_DENIED') toast.error('Failed to share screen'); }
    } else { await stopScreenShare(); }
  };

  const stopScreenShare = async () => {
    const client = clientRef.current; const st = screenTrackRef.current;
    if (st && client) { try { await client.unpublish(st); } catch (e) { console.warn('[Agora] Unpublish screen error:', e); } st.close(); screenTrackRef.current = null; }
    const cam = localVideoTrackRef.current;
    if (cam && client) { try { await client.publish(cam); if (!isVideoOff) playLocalVideo(cam); } catch (e) { console.warn('[Agora] Republish camera error:', e); } }
    setIsScreenSharing(false);
  };

  // ── Speaker / Earpiece Toggle ──
  const toggleSpeaker = async () => {
    const newSpeaker = !isSpeaker;
    setIsSpeaker(newSpeaker);

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioOutputs = devices.filter(d => d.kind === 'audiooutput');

      if (audioOutputs.length > 1) {
        // Try to find earpiece (usually has 'earpiece' or 'handset' in label) vs speaker
        let targetDevice;
        if (newSpeaker) {
          targetDevice = audioOutputs.find(d => d.label.toLowerCase().includes('speaker')) || audioOutputs[0];
        } else {
          targetDevice = audioOutputs.find(d =>
            d.label.toLowerCase().includes('earpiece') ||
            d.label.toLowerCase().includes('handset') ||
            d.label.toLowerCase().includes('headphone') ||
            d.label.toLowerCase().includes('bluetooth')
          ) || audioOutputs[audioOutputs.length - 1];
        }

        // Try setSinkId on all audio elements
        const audioElements = document.querySelectorAll('audio, video');
        for (const el of audioElements) {
          if (typeof el.setSinkId === 'function') {
            try { await el.setSinkId(targetDevice.deviceId); } catch (e) { console.warn('[Audio] setSinkId error:', e); }
          }
        }
      }
    } catch (e) {
      // Mobile browsers often don't support setSinkId; OS handles routing
      console.warn('[Audio] Speaker toggle fallback:', e);
    }

    toast.info(newSpeaker ? 'Speaker ON' : 'Earpiece mode');
  };

  // ── Camera Flip (Agora getCameras + facingMode fallback for mobile) ──
  const flipCamera = async () => {
    const client = clientRef.current;
    if (!client) return;

    try {
      // Stop & close current video track
      const oldTrack = localVideoTrackRef.current;
      if (oldTrack) {
        try { await client.unpublish(oldTrack); } catch (e) { console.warn('[Agora] Unpublish old camera:', e); }
        oldTrack.stop();
        oldTrack.close();
        localVideoTrackRef.current = null;
      }

      let newTrack;
      if (cameras.length >= 2) {
        // Desktop/enumerable cameras: use cameraId
        const nextIdx = (currentCameraIdx + 1) % cameras.length;
        newTrack = await AgoraRTC.createCameraVideoTrack({ cameraId: cameras[nextIdx].deviceId });
        setCurrentCameraIdx(nextIdx);
      } else {
        // Mobile fallback: use facingMode constraint
        const newMode = facingMode === 'user' ? 'environment' : 'user';
        newTrack = await AgoraRTC.createCameraVideoTrack({
          encoderConfig: '480p_1',
          facingMode: newMode,
        });
        setFacingMode(newMode);
      }

      localVideoTrackRef.current = newTrack;
      playLocalVideo(newTrack);
      await client.publish(newTrack);
      setIsVideoOff(false);
    } catch (err) {
      console.error('[Camera] Flip failed:', err);
      toast.error('Failed to switch camera');
    }
  };

  // ── Add Participant ──
  const openAddParticipant = async () => {
    if (remoteUsers.length >= MAX_USERS - 1) {
      toast.error(`Maximum ${MAX_USERS} participants allowed`);
      return;
    }
    try {
      const res = await userAPI.getUsers();
      setContacts(res.data.filter(u => u.id !== user?.id));
    } catch (e) {
      console.error('[Call] Failed to load contacts:', e);
      setContacts([]);
    }
    setShowAddParticipant(true);
  };

  const inviteParticipant = (contact) => {
    if (remoteUsers.length >= MAX_USERS - 1) {
      toast.error(`Maximum ${MAX_USERS} participants allowed`);
      return;
    }
    if (socket) {
      socket.emit('call_invite', {
        channel_id: channelId,
        target_user_id: contact.id,
        caller_name: user?.name,
        is_video: isVideo,
      });
      toast.success(`Invited ${contact.name}`);
    }
    setShowAddParticipant(false);
  };

  const endCall = async () => {
    endedRef.current = true;
    await cleanup();
    if (onEndCall) onEndCall(channelId);
    if (aiNotesEnabled) {
      try {
        const { aiAPI, messageAPI } = await import('../utils/api');
        toast.info('Generating AI meeting notes...');
        const response = await aiAPI.meetingNotes(channelId);
        await messageAPI.createMessage({ channel_id: channelId, content: response.data.notes, message_type: 'text' });
        toast.success('Meeting notes saved!');
      } catch (e) { console.error('[AI] Failed to generate notes:', e); toast.error('Failed to generate notes'); }
    }
    navigate('/', { replace: true });
  };

  useEffect(() => {
    const t = localVideoTrackRef.current;
    if (t && !isVideoOff && !isScreenSharing) playLocalVideo(t);
  }, [isVideoOff, isScreenSharing, playLocalVideo]);

  // Total participants (local + remote)
  const totalUsers = 1 + remoteUsers.length;

  // Status helpers
  const statusColor = { 'initializing': 'bg-yellow-500', 'requesting-permissions': 'bg-yellow-500', 'joining': 'bg-blue-500', 'connected': 'bg-green-500', 'failed': 'bg-red-500', 'reconnecting': 'bg-orange-500' }[callStatus] || 'bg-gray-500';
  const statusText = { 'initializing': 'Initializing...', 'requesting-permissions': 'Requesting permissions...', 'joining': `Joining... (${joinAttempts}/3)`, 'connected': `${totalUsers} ${totalUsers === 1 ? 'person' : 'people'} in call`, 'failed': 'Failed', 'reconnecting': 'Reconnecting...' }[callStatus] || callStatus;

  // ── Grid class based on user count ──
  const getGridClass = () => {
    if (totalUsers === 1) return 'grid-cols-1';
    if (totalUsers === 2) return 'grid-cols-1 grid-rows-2';
    if (totalUsers === 3) return 'grid-cols-2';
    if (totalUsers === 4) return 'grid-cols-2 grid-rows-2';
    return 'grid-cols-2 grid-rows-3'; // 5-6 users: 2x3 grid
  };

  return (
    <div className="fixed inset-0 bg-[#0F172A] z-50 flex flex-col h-[100dvh]" data-testid="video-call-view">
      {/* Status Banner */}
      {callStatus !== 'connected' && (
        <div className={`${statusColor} text-white text-center py-2 px-4 text-sm font-medium z-[10000] flex items-center justify-center gap-2 shrink-0`} data-testid="call-status-banner">
          {['reconnecting','joining','initializing','requesting-permissions'].includes(callStatus) && (
            <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          )}
          <span>{statusText}</span>
        </div>
      )}

      {/* Participant count when connected */}
      {callStatus === 'connected' && (
        <div className="bg-green-500/80 text-white text-center py-1.5 px-4 text-xs font-medium z-[10000] shrink-0" data-testid="participant-count-banner">
          {totalUsers} {totalUsers === 1 ? 'person' : 'people'} in call
        </div>
      )}

      {/* Error + Retry */}
      {callStatus === 'failed' && callError && (
        <div className="bg-red-900/80 text-white p-4 text-center z-[10000] shrink-0" data-testid="call-error-banner">
          <p className="text-sm mb-3">{callError}</p>
          <div className="flex gap-3 justify-center">
            <button onClick={handleRetry} className="flex items-center gap-2 px-4 py-2 bg-white/20 text-white rounded-lg border border-white/30 active:scale-95" data-testid="retry-call-button">
              <RotateCcw className="text-white w-4 h-4" /> Retry
            </button>
            <button onClick={() => { if (onEndCall) onEndCall(channelId); navigate(-1); }} className="px-4 py-2 bg-white/10 text-white rounded-lg border border-white/20 active:scale-95" data-testid="exit-call-button">
              Exit
            </button>
          </div>
        </div>
      )}

      {/* Video Grid */}
      <div className="flex-1 relative z-0 p-2 overflow-hidden" style={{ marginBottom: '130px' }} data-testid="video-grid">
        <div className={`grid gap-2 h-full ${getGridClass()}`}>
          {/* Local Video */}
          <div className={`relative overflow-hidden rounded-lg bg-black ${totalUsers === 3 ? '' : ''}`} data-testid="local-video">
            <div ref={localVideoRef} id="local-player" className="w-full h-full bg-black rounded-lg flex items-center justify-center" style={{ minHeight: '140px' }}>
              {(isVideoOff && !isScreenSharing) && (
                <div className="text-white text-center">
                  <div className="w-16 h-16 rounded-full bg-[#4338CA] mx-auto flex items-center justify-center text-xl font-bold mb-1">{user?.name?.charAt(0).toUpperCase()}</div>
                  <p className="text-xs text-white/80 mt-1">You</p>
                </div>
              )}
            </div>
            <div className="absolute bottom-2 left-2 bg-black/70 px-2.5 py-0.5 rounded-full text-white text-xs font-medium backdrop-blur-sm">
              You {isMuted && '(Muted)'} {isScreenSharing && '(Sharing)'}
            </div>
          </div>

          {/* Remote Videos */}
          {remoteUsers.map((ru, idx) => (
            <div key={ru.uid} className={totalUsers === 3 && idx === 1 ? 'col-span-2 max-w-[50%] mx-auto' : ''}>
              <RemoteVideoPlayer uid={ru.uid} videoTrack={ru.videoTrack} audioTrack={ru.audioTrack} userName={uidNameMap[ru.uid] || ru.userName} />
            </div>
          ))}
        </div>
      </div>

      {/* Add Participant Overlay */}
      {showAddParticipant && (
        <div className="fixed inset-0 z-[100001] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center" data-testid="add-participant-overlay">
          <div className="bg-[#1E293B] rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm max-h-[70vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="text-white font-bold">Add Participant</h3>
              <button onClick={() => setShowAddParticipant(false)} className="text-white/60 active:scale-90" data-testid="close-add-participant">
                <X className="w-5 h-5" />
              </button>
            </div>
            <ScrollArea className="flex-1 p-3">
              {contacts.length === 0 ? (
                <p className="text-white/40 text-center py-8 text-sm">No contacts available</p>
              ) : (
                <div className="space-y-1">
                  {contacts.map(c => (
                    <button
                      key={c.id}
                      onClick={() => inviteParticipant(c)}
                      className="flex items-center gap-3 w-full p-3 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors text-left"
                      data-testid={`invite-contact-${c.id}`}
                    >
                      <Avatar className="w-10 h-10">
                        <AvatarFallback className="bg-[#4338CA] text-white text-sm">
                          {c.name.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-white text-sm font-medium">{c.name}</p>
                        <p className="text-white/40 text-xs">{c.phone || c.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      )}

      {/* Controls — Two rows for all buttons */}
      <div className="fixed bottom-0 left-0 right-0 z-[99999] bg-black/80 backdrop-blur-md pt-3 pb-4 px-4 mb-0" style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' }} data-testid="call-controls">
        {/* Row 1: Secondary controls */}
        <div className="flex items-center justify-center gap-4 mb-3">
          <ControlButton onClick={toggleSpeaker} active={isSpeaker} small testId="toggle-speaker-button">
            {isSpeaker ? <Volume2 className="text-white w-5 h-5" /> : <Headphones className="text-white w-5 h-5" />}
          </ControlButton>
          <ControlButton onClick={flipCamera} small testId="flip-camera-button">
            <SwitchCamera className="text-white w-5 h-5" />
          </ControlButton>
          <ControlButton onClick={openAddParticipant} small testId="add-participant-button">
            <UserPlus className="text-white w-5 h-5" />
          </ControlButton>
          <ControlButton onClick={toggleScreenShare} active={isScreenSharing} small testId="toggle-screen-share-button">
            {isScreenSharing ? <MonitorOff className="text-white w-5 h-5" /> : <Monitor className="text-white w-5 h-5" />}
          </ControlButton>
        </div>
        {/* Row 2: Primary controls */}
        <div className="flex items-center justify-center gap-6">
          <ControlButton onClick={toggleMute} active={!isMuted} danger={isMuted} testId="toggle-mute-button">
            {isMuted ? <MicOff className="text-white w-6 h-6" /> : <Mic className="text-white w-6 h-6" />}
          </ControlButton>
          <ControlButton onClick={toggleVideo} active={!isVideoOff} danger={isVideoOff} testId="toggle-video-button">
            {isVideoOff ? <VideoOff className="text-white w-6 h-6" /> : <VideoIcon className="text-white w-6 h-6" />}
          </ControlButton>
          <ControlButton onClick={endCall} danger large testId="end-call-button">
            <PhoneOff className="text-white w-7 h-7" />
          </ControlButton>
        </div>
      </div>
    </div>
  );
};

export default VideoCallView;
